import { useEffect, useRef, useState } from "react";
import type { AccentColor, Layer } from "shared";
import { LAYER_ACCENT_COLORS } from "shared";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { IconButton } from "@/components/primitives/IconButton";
import {
	IconChevronDown,
	IconEye,
	IconEyeOff,
	IconMoreHorizontal,
	IconPencil,
	IconPlus,
	IconTrash,
	IconUpload,
} from "@/components/primitives/icons";
import { Popover } from "@/components/primitives/Popover";
import { cn } from "@/lib/cn";
import { EntriesList, type EntryListItem } from "./EntriesList";


type Props = {
	layers: Layer[];
	activeLayerId: string | null;
	visibility: Record<string, boolean>;
	onSelect: (layerId: string) => void;
	onToggleVisibility: (layerId: string) => void;
	onColorChange: (layerId: string, color: AccentColor) => void;
	onRename: (layerId: string, title: string) => void;
	onDelete: (layerId: string) => void;
	onAdd: () => void;
	onImport?: () => void;
	adding?: boolean;
	deleting?: boolean;
	readOnly?: boolean;
	canDeleteLayers?: boolean;
	entryItems: EntryListItem[];
	selectedEntryId: string | null;
	onSelectEntry: (entryId: string) => void;
	entriesLoading?: boolean;
	className?: string;
};

const colorSwatch: Record<AccentColor, string> = {
	teal: "bg-teal-500",
	orange: "bg-orange-500",
	violet: "bg-violet-500",
	pink: "bg-pink-500",
	amber: "bg-amber-500",
	blue: "bg-blue-500",
	emerald: "bg-emerald-500",
	rose: "bg-rose-500",
};

function layerAccent(layer: Layer, index: number): AccentColor {
	const c = layer.style?.color;
	if (c && (LAYER_ACCENT_COLORS as readonly string[]).includes(c)) {
		return c as AccentColor;
	}
	return LAYER_ACCENT_COLORS[index % LAYER_ACCENT_COLORS.length];
}

export function LayerPanel({
	layers,
	activeLayerId,
	visibility,
	onSelect,
	onToggleVisibility,
	onColorChange,
	onRename,
	onDelete,
	onAdd,
	onImport,
	adding,
	deleting,
	readOnly = false,
	canDeleteLayers = true,
	entryItems,
	selectedEntryId,
	onSelectEntry,
	entriesLoading,
	className,
}: Props) {
	const [editingId, setEditingId] = useState<string | null>(null);
	const [draft, setDraft] = useState("");
	const [collapsed, setCollapsed] = useState(false);
	const [colorOpenId, setColorOpenId] = useState<string | null>(null);
	const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
	const colorTriggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const menuTriggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const colorAnchorRef = useRef<HTMLElement | null>(null);
	const menuAnchorRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!editingId) return;
		const layer = layers.find((l) => l.id === editingId);
		if (!layer) {
			setEditingId(null);
			return;
		}
		setDraft(layer.title);
	}, [editingId, layers]);

	const startEdit = (layer: Layer) => {
		if (readOnly) return;
		onSelect(layer.id);
		setEditingId(layer.id);
		setDraft(layer.title);
		setMenuOpenId(null);
		setColorOpenId(null);
	};

	const openColorFor = (layerId: string, trigger: HTMLButtonElement | null) => {
		colorAnchorRef.current = trigger;
		setColorOpenId((id) => (id === layerId ? null : layerId));
		setMenuOpenId(null);
	};

	const openMenuFor = (layerId: string, trigger: HTMLButtonElement | null) => {
		menuAnchorRef.current = trigger;
		setMenuOpenId((id) => (id === layerId ? null : layerId));
		setColorOpenId(null);
	};

	const commitEdit = () => {
		if (!editingId) return;
		const layer = layers.find((l) => l.id === editingId);
		const next = draft.trim();
		setEditingId(null);
		if (!layer || !next || next === layer.title) return;
		onRename(editingId, next);
	};

	const cancelEdit = () => {
		setEditingId(null);
	};

	const openColorLayer = colorOpenId
		? layers.find((l) => l.id === colorOpenId)
		: null;
	const openColorIndex = openColorLayer
		? layers.findIndex((l) => l.id === openColorLayer.id)
		: -1;
	const openColorAccent =
		openColorLayer && openColorIndex >= 0
			? layerAccent(openColorLayer, openColorIndex)
			: null;

	return (
		<div className={cn("w-full max-w-[20rem]", className)}>
			<FloatingCard
				as="aside"
				className={cn(
					"flex max-h-[min(70vh,36rem)] w-full flex-col overflow-hidden p-2",
					"max-md:max-h-[45vh] max-md:rounded-b-none",
				)}
			>
				<button
					type="button"
					className="flex h-8 w-full items-center justify-between gap-2 rounded-[var(--radius-control)] px-1.5 text-left"
					onClick={() => setCollapsed((v) => !v)}
				>
					<span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
						Ebenen
					</span>
					<IconChevronDown
						className={cn(
							"text-fg-muted transition-transform",
							collapsed && "-rotate-90",
						)}
					/>
				</button>

				{!collapsed ? (
					<>
						<ul className="min-h-0 max-h-[40%] space-y-0.5 overflow-y-auto overflow-x-hidden">
							{layers.map((layer, index) => {
								const accent = layerAccent(layer, index);
								const visible = visibility[layer.id] ?? layer.defaultVisible;
								const active = layer.id === activeLayerId;
								const editing = editingId === layer.id;
								return (
									<li key={layer.id}>
										<div
											className={cn(
												"rounded-[var(--radius-control)] px-1 py-1 text-sm",
												active && "bg-teal-500/10",
											)}
										>
											<div className="flex items-center gap-0.5">
												<IconButton
													label={visible ? "Ausblenden" : "Einblenden"}
													onClick={() => onToggleVisibility(layer.id)}
												>
													{visible ? <IconEye /> : <IconEyeOff />}
												</IconButton>
												{editing ? (
													<input
														autoFocus
														value={draft}
														onChange={(e) => setDraft(e.target.value)}
														onBlur={commitEdit}
														onKeyDown={(e) => {
															if (e.key === "Enter") {
																e.preventDefault();
																commitEdit();
															}
															if (e.key === "Escape") {
																e.preventDefault();
																cancelEdit();
															}
														}}
														className="h-7 min-w-0 flex-1 rounded-[var(--radius-control)] bg-surface px-2.5 text-sm font-medium text-fg outline-none ring-2 ring-teal-500"
														aria-label="Ebenenname"
													/>
												) : (
													<button
														type="button"
														onClick={() => onSelect(layer.id)}
														onDoubleClick={() => startEdit(layer)}
														className="min-w-0 flex-1 truncate px-1 text-left text-sm font-medium"
														title={
															readOnly
																? layer.title
																: "Doppelklick zum Umbenennen"
														}
													>
														{layer.title}
													</button>
												)}
												{!readOnly ? (
													<button
														type="button"
														title="Farbe"
														ref={(el) => {
															if (el) colorTriggerRefs.current.set(layer.id, el);
															else colorTriggerRefs.current.delete(layer.id);
														}}
														onClick={(e) => {
															openColorFor(layer.id, e.currentTarget);
														}}
														className={cn(
															"h-4 w-4 shrink-0 rounded-full ring-offset-1 ring-offset-surface",
															colorSwatch[accent],
															colorOpenId === layer.id && "ring-2 ring-fg",
														)}
													/>
												) : (
													<span
														className={cn(
															"h-3 w-3 shrink-0 rounded-full",
															colorSwatch[accent],
														)}
													/>
												)}
												{!readOnly ? (
													<IconButton
														label="Mehr"
														ref={(el) => {
															if (el) menuTriggerRefs.current.set(layer.id, el);
															else menuTriggerRefs.current.delete(layer.id);
														}}
														onClick={(e) => {
															openMenuFor(layer.id, e.currentTarget);
														}}
													>
														<IconMoreHorizontal />
													</IconButton>
												) : null}
											</div>
										</div>
									</li>
								);
							})}
						</ul>
						{!readOnly ? (
							<Button
								variant="ghost"
								size="sm"
								className="mt-1 w-full shrink-0 justify-start"
								onClick={onAdd}
								disabled={adding}
							>
								<IconPlus />
								{adding ? "…" : "Ebene"}
							</Button>
						) : null}
						{!readOnly && onImport ? (
							<Button
								variant="ghost"
								size="sm"
								className="w-full shrink-0 justify-start"
								onClick={onImport}
							>
								<IconUpload />
								Importieren
							</Button>
						) : null}
						<EntriesList
							items={entryItems}
							selectedEntryId={selectedEntryId}
							onSelect={onSelectEntry}
							loading={entriesLoading}
						/>
					</>
				) : null}
			</FloatingCard>

			{colorOpenId && openColorAccent ? (
				<Popover
					open
					onOpenChange={(next) => {
						if (!next) setColorOpenId(null);
					}}
					anchorRef={colorAnchorRef}
					placementKey={colorOpenId}
					align="end"
					role="listbox"
					aria-label="Ebenenfarbe"
					className="flex w-[7.5rem] flex-wrap gap-1 p-1.5"
				>
					{LAYER_ACCENT_COLORS.map((color) => (
						<button
							key={color}
							type="button"
							title={color}
							role="option"
							aria-selected={openColorAccent === color}
							onClick={() => {
								onColorChange(colorOpenId, color);
								setColorOpenId(null);
							}}
							className={cn(
								"h-5 w-5 rounded-full",
								colorSwatch[color],
								openColorAccent === color && "ring-2 ring-fg",
							)}
						/>
					))}
				</Popover>
			) : null}

			{menuOpenId ? (
				<Popover
					open
					onOpenChange={(next) => {
						if (!next) setMenuOpenId(null);
					}}
					anchorRef={menuAnchorRef}
					placementKey={menuOpenId}
					align="end"
					role="menu"
					className="min-w-[8rem] py-1"
				>
					<button
						type="button"
						role="menuitem"
						className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-raised"
						onClick={() => {
							const layer = layers.find((l) => l.id === menuOpenId);
							if (layer) startEdit(layer);
						}}
					>
						<IconPencil />
						Umbenennen
					</button>
					{canDeleteLayers ? (
						<button
							type="button"
							role="menuitem"
							disabled={deleting}
							className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-rose-500 hover:bg-rose-500/10 disabled:opacity-50"
							onClick={() => {
								const id = menuOpenId;
								setMenuOpenId(null);
								onDelete(id);
							}}
						>
							<IconTrash />
							Löschen
						</button>
					) : null}
				</Popover>
			) : null}
		</div>
	);
}
